import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { sectionId } from '../utils/routes';

const WhyUsSection = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.substring(0, 2) || 'it';
  
  const itemKeys = ['speed', 'quality', 'support', 'personal', 'monitoring', 'no_template'];
  
  const icons = {
    speed: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    quality: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
      </svg>
    ),
    support: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
    personal: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>
    ),
    monitoring: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
    ),
    no_template: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    )
  };

  // Get results items as array
  const resultsItems = t('why_us.results.items', { returnObjects: true }) || [];
  const idealForItems = t('why_us.ideal_for.items', { returnObjects: true }) || [];

  return (
    <section id={sectionId(lang, 'why_us')} className="py-32 bg-[#0a0a0a] relative overflow-hidden">
      {/* Decorative line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FFD700]/30 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="grid lg:grid-cols-2 gap-16 mb-20">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-[#FFD700] text-xs tracking-[0.4em] uppercase mb-6 block">
              {t('why_us.subtitle')}
            </span>
            <h2 className="font-display text-6xl md:text-7xl lg:text-8xl text-white leading-[0.9]">
              {t('why_us.title')}
              <br />
              <span className="text-outline">{t('why_us.title_outline')}</span>
            </h2>
          </motion.div>
          
          <motion.div
            className="flex items-end"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <p className="text-white/80 text-lg font-light leading-relaxed max-w-md">
              {t('why_us.description')}
            </p>
          </motion.div>
        </div>

        {/* Features Grid (up to 6 items) */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-24">
          {itemKeys.map((key, index) => (
            <motion.div
              key={key}
              className="group p-8 md:p-10 glass-card glow-hover shine-effect relative overflow-hidden"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -5, transition: { duration: 0.3 } }}
            >
              {/* Animated corner accents */}
              <div className="absolute top-0 left-0 w-16 h-16 border-t border-l border-[#FFD700]/20 group-hover:border-[#FFD700]/50 transition-colors duration-500" />
              <div className="absolute bottom-0 right-0 w-16 h-16 border-b border-r border-[#FFD700]/20 group-hover:border-[#FFD700]/50 transition-colors duration-500" />
              
              {/* Number */}
              <span className="absolute top-6 right-6 font-display text-6xl text-white/5 group-hover:text-[#FFD700]/15 transition-colors duration-500">
                {String(index + 1).padStart(2, '0')}
              </span>
              
              <div className="relative z-10">
                {/* Icon with glow */}
                <motion.div 
                  className="text-[#FFD700] mb-6"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <div className="relative">
                    {icons[key]}
                    <div className="absolute inset-0 blur-lg opacity-50 group-hover:opacity-100 transition-opacity duration-500">
                      {icons[key]}
                    </div>
                  </div>
                </motion.div>
                
                {/* Title */}
                <h3 className="font-display text-2xl md:text-3xl text-white mb-4 group-hover:text-[#FFD700] transition-colors duration-500 text-glow-hover">
                  {t(`why_us.items.${key}.title`)}
                </h3>
                
                {/* Description */}
                <p className="text-white/85 font-light leading-relaxed mb-4">
                  {t(`why_us.items.${key}.description`)}
                </p>
                
                {/* Highlight phrase */}
                <p className="text-[#FFD700] text-sm font-medium tracking-wide">
                  {t(`why_us.items.${key}.highlight`)}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* RISULTATI REALI Block */}
        <motion.div
          className="mb-24 text-center"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h3 className="font-display text-4xl md:text-5xl text-white mb-4">
            {t('why_us.results.title')} <span className="text-outline">{t('why_us.results.title_outline')}</span>
          </h3>
          <p className="text-white/80 text-lg mb-8">{t('why_us.results.subtitle')}</p>
          
          <div className="flex flex-wrap justify-center gap-6 md:gap-12">
            {Array.isArray(resultsItems) && resultsItems.map((item, index) => (
              <motion.div
                key={item}
                className="flex items-center gap-3"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <span className="text-[#FFD700] text-xl">✓</span>
                <span className="text-white/80">{item}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Bottom row: 3 blocks */}
        <div className="grid md:grid-cols-3 gap-8">
          
          {/* PER CHI È IDEALE */}
          <motion.div
            className="p-8 border border-white/10 text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h4 className="font-display text-2xl md:text-3xl text-white mb-6">
              {t('why_us.ideal_for.title')} <span className="text-[#FFD700]">{t('why_us.ideal_for.title_outline')}</span>
            </h4>
            <div className="flex flex-wrap justify-center gap-3">
              {Array.isArray(idealForItems) && idealForItems.map((item) => (
                <span
                  key={item}
                  className="px-4 py-2 border border-[#FFD700]/30 text-white/80 text-sm hover:bg-[#FFD700]/10 transition-colors"
                >
                  {item}
                </span>
              ))}
            </div>
          </motion.div>

          {/* SENZA IMPEGNO */}
          <motion.div
            className="p-8 border border-[#FFD700]/30 bg-[#FFD700]/5 text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h4 className="font-display text-2xl md:text-3xl text-[#FFD700] mb-4">
              {t('why_us.no_commitment.title')} {t('why_us.no_commitment.title_outline')}
            </h4>
            <p className="text-white font-medium text-lg mb-2">
              {t('why_us.no_commitment.line1')}
            </p>
            <p className="text-white/70">
              {t('why_us.no_commitment.line2')}
            </p>
          </motion.div>

          {/* VALORE REALE + TRASPARENZA */}
          <motion.div
            className="p-8 border border-[#FFD700]/20 bg-[#FFD700]/5 text-center relative overflow-hidden"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Decorative corners */}
            <div className="absolute top-0 right-0 w-12 h-12 border-l border-b border-[#FFD700]/20" />
            <div className="absolute bottom-0 left-0 w-12 h-12 border-r border-t border-[#FFD700]/20" />
            
            <div className="w-10 h-10 border border-[#FFD700]/40 flex items-center justify-center mx-auto mb-4">
              <svg className="w-5 h-5 text-[#FFD700]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h4 className="font-display text-2xl md:text-3xl text-[#FFD700] mb-2">
              {t('why_us.value.title')} <span className="text-white">{t('why_us.value.title_outline')}</span>
            </h4>
            <p className="text-white/80 text-sm mb-3">{t('why_us.value.description')}</p>
            <p className="text-white/70 text-sm italic">
              {t('why_us.value.tax_note')}
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WhyUsSection;
